import { X } from 'lucide-react';
import { Dispatch, SetStateAction, useState } from 'react';
import classNames from 'classnames';
import Modal from '@/components/v2/common/Modal';
import i18n from '@/settings/i18n';
import { useProductPageContext } from '../../../hooks/useProductPageContext';
import { Stars } from '../../main/right/product/ReviewsSummary';
import ProductReviews from './ProductReviews';
import CompanyReviews from './CompanyReviews';
import Loading from '@/components/commons/Loading';
import { useCompanyReviewsContext } from '@/utils/hooks/reviews/useCompanyReviewsContext';
import { formatStoreDecimal, formatStoreNumber } from '@/lib/intl-format';

const t = i18n.init('product');

const Header = ({
  setShowModal,
}: {
  setShowModal: Dispatch<SetStateAction<boolean>>;
}) => (
  <div className="flex shrink-0 items-center justify-between bg-white p-4 text-sm shadow">
    <div>
      <h1 className="w-full text-base font-extrabold leading-5">
        {t`Customer Reviews & Ratings`}
      </h1>
    </div>
    <button
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white  transition duration-150 ease-in-out hover:scale-110 focus:outline-none"
      onClick={() => setShowModal(false)}
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);

type Tab = 'company' | 'product';

const Banner = ({ stars, count }: { stars: number; count: number }) => (
  <div
    className="flex items-center justify-between overflow-auto p-4"
    // eslint-disable-next-line react/forbid-dom-props
    style={{
      background:
        'linear-gradient(90deg, rgba(214, 255, 226, 0.50) 0%, rgba(146, 247, 250, 0.50) 100%)',
    }}
  >
    <div className="flex items-center gap-x-4">
      <div>
        <span className="text-2xl font-bold">{formatStoreDecimal(stars)}</span>
        <span className="text-base font-medium">/5</span>
      </div>
      <Stars stars={stars} className="h-5" />
      {count > 0 && (
        <span>
          {formatStoreNumber(count)} {t`customer reviews`}
        </span>
      )}
    </div>
  </div>
);

const Footer = ({
  activeTab,
  setActiveTab,
  showTabs,
}: {
  activeTab: Tab;
  setActiveTab: Dispatch<SetStateAction<Tab>>;
  showTabs: boolean;
}) => {
  if (!showTabs) {
    return null;
  }

  return (
    <div className="flex gap-3 p-4">
      <button
        onClick={() => setActiveTab('company')}
        className={classNames(
          'flex-1 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors',
          activeTab === 'company'
            ? 'border-[#00B67A] bg-[#00B67A] text-white'
            : 'border-gray-500 bg-white text-gray-700',
        )}
      >
        {t`Reebelo Reviews`}
      </button>
      <button
        onClick={() => setActiveTab('product')}
        className={classNames(
          'flex-1 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors',
          activeTab === 'product'
            ? 'border-[#00B67A] bg-[#00B67A] text-white'
            : 'border-gray-500 bg-white text-gray-700',
        )}
      >
        {t`Product Reviews`}
      </button>
    </div>
  );
};

const Content = () => {
  const { additionalInfo } = useProductPageContext();

  const { company } = useCompanyReviewsContext();

  const reviews = additionalInfo.reviews?.reviews ?? [];

  const [activeTab, setActiveTab] = useState<Tab>(
    reviews.length > 0 ? 'product' : 'company',
  );

  if (company?.reviews == null && activeTab === 'company') {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  const stars =
    activeTab === 'company'
      ? company?.business.score.stars ?? 4.7
      : additionalInfo.reviews?.summary.stars ?? 4.5;

  const count =
    activeTab === 'company'
      ? company?.business.numberOfReviews.total ?? 85000
      : additionalInfo.reviews?.summary.count ?? 0;

  const ReviewContent =
    activeTab === 'company' && company?.reviews ? (
      <CompanyReviews reviews={company.reviews} />
    ) : (
      <ProductReviews />
    );

  return (
    <div className="flex flex-1 flex-col overflow-hidden text-sm">
      <Banner stars={stars} count={count} />
      <div className="flex-1 overflow-y-auto p-4">{ReviewContent}</div>
      <Footer
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showTabs={reviews.length > 0}
      />
    </div>
  );
};

const ReviewsModal = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}) => (
  <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
    <Header setShowModal={setShowModal} />
    <Content />
  </Modal>
);

export default ReviewsModal;
